import { DevTool } from "@hookform/devtools";
import { FC, PropsWithChildren, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilState, useRecoilValue } from "recoil";

import {
  useConditionalFormRevalidate,
  useCrudEditContext,
  useYupValidationResolver,
} from "@ryde/redux";
import {
  IExtendableDto,
  IStationRouteDto,
  KeyboardKeys,
  Log,
  logBoolean,
  shouldDebugForms,
  shouldResetOnAddNew,
  shouldResetOnEditOrDuplicate,
  stationRouteEditHasMetadataChangedAtom,
  stationRouteHasUnsavedStationsChangeAtom,
  yupValidationSchemaSF,
} from "@ryde/shared";

export const StationRouteEditFormTagWrapper: FC<PropsWithChildren> = ({
  children,
}) => {
  const {
    data: { dto, isEdit, isDuplicate },
    entity,
    mode,
  } = useCrudEditContext<IStationRouteDto>();

  const yupSchemaDynamic = useRecoilValue(yupValidationSchemaSF(entity));
  const yupDynamicResolver = useYupValidationResolver(yupSchemaDynamic, entity);
  const methods = useForm<IExtendableDto>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    resolver: yupDynamicResolver,
  });

  const {
    reset,
    clearErrors,
    control,
    setValue,
    getValues,
    trigger,
    formState: { isDirty },
  } = methods;

  const [
    stationRouteHasUnsavedStationChange,
    setStationRouteHasUnsavedStationChange,
  ] = useRecoilState(stationRouteHasUnsavedStationsChangeAtom);
  const [
    stationRouteEditHasMetadataChanged,
    setStationRouteEditHasMetadataChanged,
  ] = useRecoilState(stationRouteEditHasMetadataChangedAtom);

  useEffect(() => {
    /* initial cleanup of previous form state values  */
    setStationRouteHasUnsavedStationChange(false);
    setStationRouteEditHasMetadataChanged(false);
  }, [
    setStationRouteEditHasMetadataChanged,
    setStationRouteHasUnsavedStationChange,
  ]);

  // MAY BE NEEDED IF MANUAL CREATION OF ROUTE IS IMPLEMENTED
  // const setStationRouteDto = useSetRecoilState(
  //   editFormDataAF([entity, mode || EditFormMode.Crud]),
  // );
  // useEffect(() => {
  //   if (!isEdit && dto === undefined) {
  //     setStationRouteDto(getNewCourseTemplate());
  //   }
  // }, [dto, isEdit, setStationRouteDto]);

  useEffect(() => {
    if (
      isDirty ||
      stationRouteHasUnsavedStationChange ||
      stationRouteEditHasMetadataChanged
    ) {
      Log.debug(
        `Station route dto changed without form reset, isDirty: ${logBoolean(
          isDirty,
        )}, metadata changed ${logBoolean(
          stationRouteEditHasMetadataChanged,
        )}, has changed stations: ${logBoolean(
          stationRouteHasUnsavedStationChange,
        )}}`,
      );
      return;
    }

    if (shouldResetOnEditOrDuplicate({ dto, isEdit, isDuplicate })) {
      Log.debug(`form reset called for entity ${entity}, dto:`, dto);
      clearErrors();
      reset({ ...dto });
    } else if (dto && shouldResetOnAddNew({ dto, isEdit, isDuplicate })) {
      Log.debug(
        `Setting initial values called for entity ${entity}, dto:`,
        dto,
      );
      clearErrors();
      Object.entries(dto).forEach(([field, value]) => {
        if (value !== undefined && value !== null) {
          setValue(field as keyof IStationRouteDto, value, {
            shouldDirty: true,
          });
        }
      });
    }
  }, [
    clearErrors,
    dto,
    entity,
    isDirty,
    isDuplicate,
    isEdit,
    reset,
    setValue,
    stationRouteEditHasMetadataChanged,
    stationRouteHasUnsavedStationChange,
  ]);

  useConditionalFormRevalidate({
    trigger,
    isEdit,
    dto,
    yupSchemaDynamic,
    getValues,
    clearErrors,
  });

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
  };

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === KeyboardKeys.ENTER) {
      event.preventDefault();
    }
  };

  return (
    <FormProvider {...methods}>
      {/* eslint-disable-next-line jsx-a11y-x/no-noninteractive-element-interactions -- intentional: suppress implicit Enter submit on the form */}
      <form
        onSubmit={handleFormSubmit}
        onKeyDown={handleKeyDown}
        noValidate
        data-e2e="station-route-form-tag"
        data-e2e-entity={entity}
        data-e2e-mode={mode}
      >
        {children}
      </form>
      {shouldDebugForms() && <DevTool control={control} />}
    </FormProvider>
  );
};
